import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";

import Modal from "../../../../components/Reusable/Modal";
import { useAppDispatch, useAppSelector } from "../../../../hooks/hooks";
import {
  createDGCoinCategory,
  fetchDGCoinCategories,
} from "../../../../features/auth/store/dgcoinCategorySlice";
import { fetchAllDGCoinProviders } from "../../../../features/auth/store/dgcoinProviderSlice";
import { CreateProps } from "../types";

export const CreateDGCoinCategoryModal: React.FC<CreateProps> = ({
  isOpen,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const [form, setForm] = useState<{
    name: string;
    description: string;
    status: "on" | "off";
    provider_id: string;
  }>({
    name: "",
    description: "",
    status: "on",
    provider_id: "",
  });
  const [image, setImage] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    dgcoinProviders,
    allDgcoinProviders,
    allLoading: providersLoading,
  } = useAppSelector((state) => state.dgcoinProviders);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchAllDGCoinProviders());
    }
  }, [dispatch, isOpen]);

  const providersForSelect =
    allDgcoinProviders.length > 0 ? allDgcoinProviders : dgcoinProviders;

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImage(e.target.files[0]);
    }
  };

  const removeImage = () => setImage(null);

  const handleClose = () => {
    if (onClose) onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", form.name);
      formData.append("description", form.description);
      formData.append("status", form.status);
      formData.append("provider_id", form.provider_id);
      if (image) {
        formData.append("image", image);
      }
      await dispatch(createDGCoinCategory(formData)).unwrap();
      toast.success("Category created!");
      handleClose();
      dispatch(fetchDGCoinCategories({}));
    } catch (err: any) {
      toast.error(err?.message || "Failed to create category");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      className="w-full max-w-md md:max-w-lg lg:max-w-xl mx-auto"
    >
      <div className="max-h-[90vh] overflow-y-auto px-1">
        <h2 className="text-lg md:text-xl font-bold mb-4">
          Create DG Coin Category
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block mb-2">Provider</label>
            <select
              name="provider_id"
              value={form.provider_id}
              onChange={handleChange}
              required
              className="w-full p-2 border rounded  dark:bg-white/10 dark:text-white"
              disabled={providersLoading}
            >
              <option value="">Select Provider</option>
              {providersForSelect.map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.name}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label className="block mb-2">Name</label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              className="w-full p-2 border rounded  dark:bg-white/10 dark:text-white"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Description</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={4}
              className="w-full p-2 border rounded  dark:bg-white/10 dark:text-white"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Status</label>
            <select
              name="status"
              value={form.status}
              onChange={handleChange}
              className="w-full p-2 border rounded  dark:bg-white/10 dark:text-white"
            >
              <option value="on">On</option>
              <option value="off">Off</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="block mb-2">Image</label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center  dark:bg-white/10 dark:text-white">
              <input
                type="file"
                id="categoryImageUpload"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              <label
                htmlFor="categoryImageUpload"
                className="flex flex-col items-center justify-center cursor-pointer"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="mb-2"
                >
                  <path
                    d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                    stroke="#718096"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M17 8L12 3L7 8"
                    stroke="#718096"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 3V15"
                    stroke="#718096"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="text-sm text-gray-600">
                  Upload from computer
                </span>
              </label>
            </div>
            {image && (
              <div className="mt-4 grid grid-cols-3 gap-4">
                <div className="relative rounded-md overflow-hidden h-20">
                  <img
                    src={URL.createObjectURL(image)}
                    alt="category"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute top-1 right-1 bg-white rounded-full p-1"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18 6L6 18"
                        stroke="#E53E3E"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6 6L18 18"
                        stroke="#E53E3E"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full bg-gradient-to-r from-[#027EC2] to-[#039EF2] h-10 text-white rounded ${
              isSubmitting ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isSubmitting ? "Creating..." : "Create Category"}
          </button>
        </form>
      </div>
    </Modal>
  );
};
